"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import PreferencesModal from "./PreferencesModal";
import CookieDecorativeCard, {
  CookieIconTile,
  CookieCloseButton,
} from "./CookieDecorativeCard";
import { getCookie, setCookie } from "@/utils/common";

const COOKIE_CONSENT_KEY = "cookie_consent";

interface CookieConsentProps {
  onAcceptAll?: () => void;
  onRejectAll?: () => void;
  onClose?: () => void;
}

export default function CookieConsent({
  onAcceptAll,
  onRejectAll,
  onClose,
}: CookieConsentProps) {
  const [showPreferences, setShowPreferences] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(!getCookie(COOKIE_CONSENT_KEY));
  }, []);

  const handleAcceptAll = () => {
    setCookie(
      COOKIE_CONSENT_KEY,
      JSON.stringify({
        necessary: true,
        analytics: true,
        functional: true,
        advertising: true,
      })
    );
    onAcceptAll?.();
    setVisible(false);
  };

  const handleRejectAll = () => {
    onRejectAll?.();
    setVisible(false);
  };

  const handleClose = () => {
    onClose?.();
    setVisible(false);
  };

  const handleSavePreferences = (prefs: {
    necessary: boolean;
    analytics: boolean;
    functional: boolean;
    advertising: boolean;
  }) => {
    setCookie(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setShowPreferences(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div className="fixed bottom-5 left-1/2 z-[9998] w-[calc(100%-2rem)] max-w-[560px] -translate-x-1/2">
        <CookieDecorativeCard>
          <div className="p-6 sm:p-7">
            {/* Top row: icon + close */}
            <div className="flex items-start justify-between">
              <CookieIconTile />
              <CookieCloseButton onClick={handleClose} />
            </div>

            {/* Title */}
            <h2 className="mt-4 text-[26px] font-extrabold leading-tight text-[#040405]">
              Cookies Settings
            </h2>

            {/* Body */}
            <p className="mt-2 text-sm leading-6 text-[#32343B]">
              We use cookies to enhance your browsing experience, serve
              personalized ads or content, and analyze our traffic. By clicking
              &quot;Accept All&quot;, you consent to our use of cookies.{" "}
              <Link
                href="/privacy-policy"
                className="font-medium text-[#0086E6] hover:underline"
              >
                Cookie Policy
              </Link>
              .
            </p>

            {/* Actions */}
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <button
                type="button"
                onClick={handleAcceptAll}
                className="border border-[#32343B] px-4 py-2.5 text-sm font-semibold text-[#040405] transition-colors hover:bg-[#32343B] hover:text-white"
              >
                Accept All
              </button>
              <button
                type="button"
                onClick={handleRejectAll}
                className="border border-[#32343B] px-4 py-2.5 text-sm font-semibold text-[#040405] transition-colors hover:bg-[#32343B] hover:text-white"
              >
                Reject All
              </button>
              <button
                type="button"
                onClick={() => setShowPreferences(true)}
                className="border border-[#32343B] px-4 py-2.5 text-sm font-semibold text-[#040405] transition-colors hover:bg-[#32343B] hover:text-white"
              >
                Manage
              </button>
            </div>
          </div>
        </CookieDecorativeCard>
      </div>

      {showPreferences && (
        <PreferencesModal
          onClose={() => setShowPreferences(false)}
          onSave={handleSavePreferences}
        />
      )}
    </>
  );
}
